<template>
  <div class="support-ticket-details px-4 pb-10">
    <div class="flex flex-col gap-6">
      <shipblu-card class="ticket-hero-card">
        <div v-if="ticket" class="ticket-hero-content">
          <div class="hero-overview">
            <p class="ticket-code">{{ $t('Ticket No.') }} {{ ticketCode }}</p>
            <h1>{{ ticket.subject || $t('Support Ticket') }}</h1>
            <p class="hero-subtitle" v-if="ticket.description">{{ ticket.description }}</p>
            <div class="hero-meta">
              <span class="status-pill" :class="statusPillClass(ticket.status)">{{ formatStatus(ticket.status) }}</span>
              <span class="priority-pill" :class="priorityClass(ticket.priority)">{{ formatPriority(ticket.priority) }}</span>
              <span class="hero-meta-item">
                <span class="material-symbols-outlined">schedule</span>
                {{ formatDateTime(ticket.created_at || ticket.created || ticket.createdAt) }}
              </span>
              <span v-if="ticket.updated_at || ticket.updated" class="hero-meta-item">
                <span class="material-symbols-outlined">history</span>
                {{ $t('Updated') }} {{ formatRelativeTime(ticket.updated_at || ticket.updated || ticket.updatedAt) }}
              </span>
            </div>
          </div>
          <div class="hero-summary">
            <div>
              <p class="label">{{ $t('Customer Name') }}</p>
              <h3>{{ ticket.contact_name || merchantSummary.contactName || '-' }}</h3>
            </div>
            <div>
              <p class="label">{{ $t('Phone Number') }}</p>
              <h3>{{ formatPhone(ticket.contact_phone || merchantSummary.contactPhone) || '-' }}</h3>
            </div>
            <div>
              <p class="label">{{ $t('Response Mode') }}</p>
              <h3>{{ formatResponseMode(ticket.response_mode) }}</h3>
            </div>
            <div>
              <p class="label">{{ $t('Assigned Agent') }}</p>
              <h3>{{ assignedAgent }}</h3>
            </div>
          </div>
        </div>
        <div v-else class="hero-loading">
          <span class="loader"></span>
          <p>{{ $t('Loading ticket details...') }}</p>
        </div>
      </shipblu-card>

      <div class="grid gap-6 xl:grid-cols-[1.6fr_1fr]">
        <div class="flex flex-col gap-6">
          <shipblu-card class="p-0 conversation-card">
            <div class="card-header">
              <div>
                <h2>{{ $t('Ticket History') }}</h2>
                <p>{{ $t('Track every update from you and the support team.') }}</p>
              </div>
            </div>

            <div v-if="loading" class="history-loading">
              <span class="loader"></span>
              <p>{{ $t('Fetching ticket history...') }}</p>
            </div>

            <div v-else-if="!orderedMessages.length" class="empty-state">
              <span class="material-symbols-outlined">forum</span>
              <p>{{ $t('No messages yet') }}</p>
              <span>{{ $t('Start the conversation by adding a reply below.') }}</span>
            </div>

            <ul v-else class="conversation-list">
              <li v-for="message in orderedMessages" :key="message.id || message.created_at" class="message-item" :class="messageBubbleClass(message)">
                <div class="avatar" :class="messageAvatarClass(message)">
                  <img v-if="message.author && message.author.avatar" :src="message.author.avatar" alt="" />
                  <span v-else>{{ getInitials(authorName(message)) }}</span>
                </div>
                <div class="message-body">
                  <div class="message-header">
                    <div class="message-author">
                      <h4>{{ authorName(message) }}</h4>
                      <span>{{ authorRole(message) }}</span>
                    </div>
                    <div class="message-meta">
                      <span>{{ formatDateTime(message.created_at || message.created || message.createdAt) }}</span>
                      <span v-if="isInternal(message)" class="internal-pill">{{ $t('Internal') }}</span>
                    </div>
                  </div>
                  <p class="message-content" v-if="message.content" v-html="formatMessageBody(message.content)"></p>
                  <p class="message-content" v-else-if="message.body" v-html="formatMessageBody(message.body)"></p>

                  <ul v-if="message.attachments && message.attachments.length" class="message-attachments">
                    <li v-for="attachment in message.attachments" :key="attachment.id || attachment.url">
                      <a :href="attachment.url || attachment.file || attachment.path" target="_blank" rel="noopener">
                        <span class="material-symbols-outlined">attach_file</span>
                        <span>{{ attachment.name || attachment.file_name || attachment.filename || $t('Attachment') }}</span>
                        <small v-if="attachment.size">{{ formatFileSize(attachment.size) }}</small>
                      </a>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </shipblu-card>

          <shipblu-card class="p-0 reply-card">
            <div class="card-header">
              <div>
                <h2>{{ $t('Add a Reply') }}</h2>
                <p>{{ $t('Share updates, files, or follow-up questions with our team.') }}</p>
              </div>
            </div>

            <form class="reply-form" @submit.prevent="submitReply">
              <label class="shipblu-label">{{ $t('Message') }}<span class="required">*</span></label>
              <vs-textarea
                name="reply message"
                rows="5"
                width="100%"
                class="shipblu-textarea"
                :disabled="replySubmitting"
                v-validate="'required|min:3'"
                v-model.trim="reply"
              />
              <span class="input-error" v-if="errors.has('reply message')">{{ $t('Please add a message for the support team') }}</span>

              <label class="shipblu-label mt-6">{{ $t('Attachments') }}</label>
              <div class="attachments-uploader" @click="triggerReplyAttachment" role="button" tabindex="0" @keyup.enter="triggerReplyAttachment" @keyup.space.prevent="triggerReplyAttachment">
                <div class="flex items-center gap-3">
                  <span class="material-symbols-outlined">cloud_upload</span>
                  <div>
                    <p>{{ $t('Drop files here or click to upload') }}</p>
                    <span>{{ $t('Max file size: 10 MB each') }}</span>
                  </div>
                </div>
                <vs-button size="small" type="border" :disabled="replySubmitting">{{ $t('Browse files') }}</vs-button>
              </div>
              <input ref="replyAttachments" class="hidden" multiple type="file" @change="handleReplyAttachmentChange" />

              <ul class="attachment-list" v-if="replyAttachments.length">
                <li v-for="(file, index) in replyAttachments" :key="file.uid" class="attachment-item">
                  <div class="flex items-center gap-3">
                    <span class="material-symbols-outlined text-primary">attach_file</span>
                    <div>
                      <p>{{ file.name }}</p>
                      <span>{{ formatFileSize(file.size) }}</span>
                    </div>
                  </div>
                  <button type="button" @click="removeReplyAttachment(index)" :disabled="replySubmitting">
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </li>
              </ul>

              <div class="reply-actions">
                <p>{{ $t('Replies are shared with the ShipBlu support team instantly.') }}</p>
                <vs-button type="filled" :loading="replySubmitting" :disabled="replySubmitting">{{ $t('Send Reply') }}</vs-button>
              </div>
            </form>
          </shipblu-card>
        </div>

        <div class="flex flex-col gap-6">
          <shipblu-card class="p-0 summary-card">
            <div class="card-header">
              <div>
                <h2>{{ $t('Ticket Summary') }}</h2>
                <p>{{ $t('Key information related to this request.') }}</p>
              </div>
            </div>

            <div v-if="ticket" class="summary-grid">
              <div>
                <p class="label">{{ $t('Request Type') }}</p>
                <h3>{{ (ticket.request_type && ticket.request_type.name) || ticket.request_type || '-' }}</h3>
              </div>
              <div>
                <p class="label">{{ $t('Category') }}</p>
                <h3>{{ (ticket.category && ticket.category.name) || ticket.category || '-' }}</h3>
              </div>
              <div>
                <p class="label">{{ $t('Priority') }}</p>
                <h3>{{ formatPriority(ticket.priority) }}</h3>
              </div>
              <div>
                <p class="label">{{ $t('Created On') }}</p>
                <h3>{{ formatDateTime(ticket.created_at || ticket.created || ticket.createdAt) }}</h3>
              </div>
              <div>
                <p class="label">{{ $t('Last Updated') }}</p>
                <h3>{{ formatDateTime(ticket.updated_at || ticket.updated || ticket.updatedAt) }}</h3>
              </div>
              <div>
                <p class="label">{{ $t('SLA Target') }}</p>
                <h3>{{ ticket.sla_target ? formatDateTime(ticket.sla_target) : $t('Not specified') }}</h3>
              </div>
            </div>
            <div v-else class="history-loading">
              <span class="loader"></span>
              <p>{{ $t('Loading summary...') }}</p>
            </div>

            <div v-if="ticket && ticket.description" class="summary-description">
              <p class="label">{{ $t('Original Description') }}</p>
              <p>{{ ticket.description }}</p>
            </div>

            <div v-if="ticket && ticket.attachments && ticket.attachments.length" class="summary-attachments">
              <p class="label">{{ $t('Submitted Attachments') }}</p>
              <ul>
                <li v-for="attachment in ticket.attachments" :key="attachment.id || attachment.url">
                  <a :href="attachment.url || attachment.file || attachment.path" target="_blank" rel="noopener">
                    <span class="material-symbols-outlined">attach_file</span>
                    <span>{{ attachment.name || attachment.file_name || attachment.filename || $t('Attachment') }}</span>
                    <small v-if="attachment.size">{{ formatFileSize(attachment.size) }}</small>
                  </a>
                </li>
              </ul>
            </div>
          </shipblu-card>

          <shipblu-card v-if="timeline.length" class="p-0 timeline-card">
            <div class="card-header">
              <div>
                <h2>{{ $t('Status Timeline') }}</h2>
                <p>{{ $t('Follow important milestones and updates.') }}</p>
              </div>
            </div>

            <ul class="timeline-list">
              <li v-for="event in timeline" :key="event.id || event.timestamp" class="timeline-item">
                <div class="timeline-marker" :class="statusPillClass(event.status)"></div>
                <div class="timeline-content">
                  <div class="timeline-header">
                    <h4>{{ formatStatus(event.status || event.label) }}</h4>
                    <span>{{ formatDateTime(event.timestamp || event.created_at || event.createdAt) }}</span>
                  </div>
                  <p v-if="event.note" class="timeline-note">{{ event.note }}</p>
                  <p v-if="event.actor" class="timeline-actor">{{ $t('By') }} {{ event.actor.name || event.actor }}</p>
                </div>
              </li>
            </ul>
          </shipblu-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ShipbluCard from '@/layouts/components/ShipbluCard.vue'
import { sendRequest } from '@/http/axios/requestHelper.js'
import common from '@/assets/utils/common'

let attachmentUID = 0

export default {
  components: {
    ShipbluCard
  },
  data () {
    return {
      ticket: null,
      messages: [],
      timeline: [],
      loading: false,
      reply: '',
      replyAttachments: [],
      replySubmitting: false,
      merchantSummary: {
        contactName: '',
        contactPhone: ''
      }
    }
  },
  computed: {
    ticketCode () {
      if (!this.ticket) return this.$route.params.ticketID
      return this.ticket.ticket_code || this.ticket.code || this.ticket.reference || this.ticket.id || this.$route.params.ticketID
    },
    orderedMessages () {
      const list = Array.isArray(this.messages) ? [...this.messages] : []
      return list.sort((a, b) => new Date(a.created_at || a.created || a.createdAt) - new Date(b.created_at || b.created || b.createdAt))
    },
    assignedAgent () {
      if (!this.ticket) return this.$t('Not assigned')
      const agent = this.ticket.assigned_agent || this.ticket.assignee || this.ticket.owner
      if (!agent) return this.$t('Not assigned')
      if (typeof agent === 'string') return agent
      return agent.name || agent.full_name || agent.email || this.$t('Not assigned')
    }
  },
  watch: {
    '$route.params.ticketID' () {
      this.loadTicket()
    }
  },
  created () {
    this.loadMerchantSummary()
    this.loadTicket()
  },
  methods: {
    async loadMerchantSummary () {
      try {
        const merchantData = await common.checkMerchantInLocalStorage(this)
        if (merchantData) {
          const userMerchant = merchantData.userMerchantData || {}
          const merchant = merchantData.merchantData || {}
          this.merchantSummary = {
            contactName: `${userMerchant.first_name || ''} ${userMerchant.last_name || ''}`.trim() || merchant.contact_person || '',
            contactPhone: userMerchant.phone || merchant.contact_phone || ''
          }
        }
      } catch (error) {
        // optional data, ignore failure
      }
    },
    loadTicket () {
      const ticketId = this.$route.params.ticketID
      if (!ticketId) return
      this.loading = true
      sendRequest(true, false, this, `support-center/tickets/${ticketId}`, 'get', null, true,
        (response) => {
          const data = response.data || {}
          this.ticket = data.ticket || data
          this.messages = data.messages || data.conversation || this.ticket.messages || []
          this.timeline = data.timeline || data.history || this.ticket.timeline || []
          if (this.ticket && this.ticket.reply_placeholder) {
            this.reply = this.ticket.reply_placeholder
          }
          this.loading = false
        },
        (error) => {
          this.loading = false
          common.notifyResponseError(this, error)
          if (error.response && error.response.status === 404) {
            this.$router.push({ name: 'merchant-support-center', params: { lang: this.$route.params.lang } }).catch(() => {})
          }
        }
      )
    },
    formatStatus (status) {
      if (!status) return this.$t('Pending')
      const normalized = status.toString().replace(/_/g, ' ')
      return normalized.replace(/\b\w/g, (char) => char.toUpperCase())
    },
    formatPriority (priority) {
      if (!priority) return this.$t('Medium')
      const normalized = priority.toString().replace(/_/g, ' ')
      return normalized.replace(/\b\w/g, (char) => char.toUpperCase())
    },
    statusPillClass (status) {
      const normalized = (status || '').toString().toLowerCase()
      if (['resolved', 'closed', 'completed', 'approved'].includes(normalized)) {
        return 'bg-emerald-100 text-emerald-700'
      }
      if (['in_progress', 'pending', 'open', 'processing'].includes(normalized)) {
        return 'bg-blue-100 text-blue-700'
      }
      if (['cancelled', 'rejected', 'failed'].includes(normalized)) {
        return 'bg-rose-100 text-rose-700'
      }
      return 'bg-slate-200 text-slate-700'
    },
    priorityClass (priority) {
      const normalized = (priority || '').toString().toLowerCase()
      if (normalized === 'high' || normalized === 'urgent') {
        return 'bg-rose-100 text-rose-700'
      }
      if (normalized === 'low') {
        return 'bg-slate-100 text-slate-600'
      }
      return 'bg-amber-100 text-amber-700'
    },
    formatDateTime (value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatRelativeTime (value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      const diff = Date.now() - date.getTime()
      const minutes = Math.floor(diff / (1000 * 60))
      if (minutes < 1) return this.$t ? this.$t('Just now') : 'Just now'
      if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
      const days = Math.floor(hours / 24)
      return `${days} ${days === 1 ? 'day' : 'days'} ago`
    },
    formatPhone (phone) {
      if (!phone) return ''
      const digits = phone.replace(/[^0-9]/g, '')
      if (digits.length < 7) return phone
      return digits.replace(/(\d{3})(\d{3})(\d+)/, '$1 $2 $3')
    },
    formatResponseMode (responseMode) {
      if (!responseMode) return this.$t('Not specified')
      if (typeof responseMode === 'string') return this.formatStatus(responseMode)
      return responseMode.display || responseMode.name || responseMode.value || this.$t('Not specified')
    },
    authorName (message) {
      if (!message) return this.$t('Unknown')
      if (message.author) {
        return message.author.name || message.author.full_name || message.author.email || this.$t('Support Team')
      }
      if (message.user) {
        return message.user.name || message.user.full_name || message.user.email
      }
      if (message.agent_name) return message.agent_name
      if (message.created_by) return message.created_by
      if (message.sender) return message.sender
      return this.$t('Support Team')
    },
    authorRole (message) {
      if (!message) return ''
      if (message.author && message.author.role) return message.author.role
      if (message.user && message.user.role) return message.user.role
      if (message.author_type) return this.formatStatus(message.author_type)
      if (this.isCustomer(message)) return this.$t('Customer')
      return this.$t('ShipBlu Support')
    },
    isCustomer (message) {
      const origin = (message && (message.origin || message.direction || message.author_type || message.type)) || ''
      const normalized = origin.toString().toLowerCase()
      if (normalized.includes('customer') || normalized.includes('merchant') || normalized.includes('client')) return true
      if (message && message.is_customer) return true
      if (message && message.author && message.author.is_customer) return true
      return false
    },
    isInternal (message) {
      if (!message) return false
      if (message.is_internal !== undefined) return Boolean(message.is_internal)
      const tags = (message.tags || []).map(tag => tag.toLowerCase())
      return tags.includes('internal')
    },
    messageBubbleClass (message) {
      return this.isCustomer(message) ? 'from-customer' : 'from-support'
    },
    messageAvatarClass (message) {
      return this.isCustomer(message) ? 'customer' : 'support'
    },
    formatMessageBody (body) {
      if (!body) return ''
      return body.replace(/\n/g, '<br>')
    },
    getInitials (name) {
      if (!name) return 'S'
      const parts = name.split(' ')
      const initials = parts.slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('')
      return initials || 'S'
    },
    formatFileSize (size) {
      if (!size && size !== 0) return ''
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
    triggerReplyAttachment () {
      if (this.replySubmitting) return
      this.$refs.replyAttachments.click()
    },
    handleReplyAttachmentChange (event) {
      const files = Array.from(event.target.files || [])
      const accepted = []
      files.forEach(file => {
        const sizeInMB = file.size / (1024 * 1024)
        if (sizeInMB > 10) {
          this.$vs.notify({
            title: this.$t('File too large'),
            text: this.$t('Please upload files smaller than 10 MB.'),
            color: 'warning',
            position: 'top-center'
          })
          return
        }
        accepted.push({
          file,
          name: file.name,
          size: file.size,
          uid: `${Date.now()}-${attachmentUID++}`
        })
      })
      this.replyAttachments = [...this.replyAttachments, ...accepted]
      event.target.value = ''
    },
    removeReplyAttachment (index) {
      this.replyAttachments.splice(index, 1)
    },
    buildReplyPayload () {
      const payload = new FormData()
      payload.append('message', this.reply)
      this.replyAttachments.forEach((file, index) => {
        payload.append(`attachments[${index}]`, file.file)
      })
      return payload
    },
    submitReply () {
      if (this.replySubmitting) return
      this.$validator.validateAll().then(valid => {
        if (!valid) return
        this.replySubmitting = true
        const payload = this.buildReplyPayload()
        sendRequest(false, false, this, `support-center/tickets/${this.ticketCode}/reply`, 'post', payload, true,
          (response) => {
            this.replySubmitting = false
            this.$vs.notify({
              title: this.$t('Reply sent'),
              text: this.$t('Your message has been shared with the ShipBlu support team.'),
              color: 'success',
              position: 'top-center'
            })
            this.reply = ''
            this.replyAttachments = []
            this.$validator.reset()
            if (response && response.data && (response.data.message || response.data.body)) {
              this.messages.push(response.data)
            } else {
              this.loadTicket()
            }
          },
          (error) => {
            this.replySubmitting = false
            common.notifyResponseError(this, error)
          }
        )
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.support-ticket-details {
  background-color: #f8fafc;
}

.ticket-hero-card {
  padding: 32px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(135deg, #ffffff 0%, #eef2ff 80%);
}

.ticket-hero-content {
  display: flex;
  flex-direction: column;
  gap: 28px;

  @media (min-width: 1024px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.hero-overview {
  max-width: 600px;

  h1 {
    font-size: 26px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 8px;
  }
}

.ticket-code {
  font-size: 14px;
  font-weight: 600;
  color: #2563eb;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
}

.hero-subtitle {
  font-size: 15px;
  color: #475569;
  margin-bottom: 16px;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;

  .hero-meta-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #475569;

    span.material-symbols-outlined {
      font-size: 18px;
      color: #2563eb;
    }
  }
}

.hero-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 18px;
  background: rgba(37, 99, 235, 0.08);
  border-radius: 20px;
  padding: 20px 24px;

  .label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #2563eb;
    margin-bottom: 4px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
  }
}

.hero-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  color: #475569;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 28px 32px 0;

  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 4px;
  }

  p {
    font-size: 14px;
    color: #64748b;
  }
}

.conversation-card,
.reply-card,
.summary-card,
.timeline-card {
  padding-bottom: 32px;
}

.history-loading,
.empty-state {
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #64748b;

  span.material-symbols-outlined {
    font-size: 36px;
    color: #94a3b8;
  }
}

.loader {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #bfdbfe;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.conversation-list {
  padding: 0 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.message-item {
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 18px;
  background: #f8fafc;
  border: 1px solid transparent;
}

.message-item.from-support {
  border-color: rgba(37, 99, 235, 0.1);
}

.message-item.from-customer {
  background: #fff7ed;
  border-color: rgba(234, 88, 12, 0.18);
}

.avatar {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #fff;
  background: #2563eb;
  overflow: hidden;

  &.customer {
    background: #f97316;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.message-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.message-author h4 {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}

.message-author span {
  display: block;
  font-size: 12px;
  color: #64748b;
}

.message-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
  color: #475569;
  align-items: center;
}

.internal-pill {
  background: rgba(234, 179, 8, 0.15);
  color: #b45309;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
}

.message-content {
  font-size: 14px;
  color: #0f172a;
  line-height: 1.6;
}

.message-attachments {
  display: flex;
  flex-direction: column;
  gap: 6px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #2563eb;
    font-size: 13px;

    span.material-symbols-outlined {
      font-size: 18px;
    }

    small {
      color: #475569;
    }
  }
}

.reply-form {
  padding: 0 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.shipblu-label {
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
}

.required {
  color: #ef4444;
  margin-left: 4px;
}

.attachments-uploader {
  border: 1px dashed #94a3b8;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: #2563eb;
    background: #eff6ff;
  }
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.attachment-item {
  background: #f1f5f9;
  border-radius: 12px;
  padding: 10px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.reply-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  p {
    font-size: 13px;
    color: #64748b;
  }
}

.summary-grid {
  padding: 0 32px 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 18px;
}

.summary-description,
.summary-attachments {
  padding: 0 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  .label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
  }

  p {
    font-size: 14px;
    color: #0f172a;
    line-height: 1.6;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 6px;

    a {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #2563eb;
    }
  }
}

.timeline-list {
  padding: 0 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.timeline-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.timeline-marker {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #cbd5f5;
  flex-shrink: 0;
}

.timeline-content {
  background: #f8fafc;
  padding: 16px 18px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;

  h4 {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
  }

  span {
    font-size: 12px;
    color: #475569;
  }
}

.timeline-note {
  font-size: 13px;
  color: #475569;
}

.timeline-actor {
  font-size: 12px;
  color: #64748b;
}

.status-pill,
.priority-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
}

.priority-pill {
  background: #fde68a;
  color: #b45309;
}

.input-error {
  font-size: 12px;
  color: #ef4444;
}

@media (max-width: 768px) {
  .ticket-hero-card {
    padding: 24px;
  }

  .card-header,
  .conversation-list,
  .reply-form,
  .summary-grid,
  .summary-description,
  .summary-attachments,
  .timeline-list {
    padding-left: 20px;
    padding-right: 20px;
  }

  .message-item {
    flex-direction: column;
  }

  .reply-actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
